import { CalendarToday } from "#webapp/src/components/chat/ai-chat/assets/CalendarToday";
import { FixMyConflicts } from "#webapp/src/components/chat/ai-chat/assets/FixMyConflicts";
import { Update } from "#webapp/src/components/chat/ai-chat/assets/Update";
import { disabled_fg_ALLROLES } from "@clockwise/design-system/tokens";
import { AddLink } from "@clockwise/icons";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { Tooltip } from "@material-ui/core";
import { SendRounded } from "@material-ui/icons";
import classNames from "classnames";
import React from "react";
import { AI_EXCLUSION_TOOLTIP } from "../../constants";
import { useUpdateSuggestedPrompt } from "../hooks/SuggestedPromptContext";
const recStyles = {
  container: "cw-font-medium group-hover:cw-text-positive-muted",
  iconContainer: "cw-border-lg cw-flex cw-items-center cw-justify-center cw-p-1 cw-rounded-md",
  icon: "cw-w-4 cw-h-4",
  tipContainer: "cw-w-48",
  tipTitle: "cw-font-bold",
  action: "cw-border-b cw-border-dashed cw-border-b-default group-hover:cw-border-b-positive-muted",
  at: "cw-text-busy group-hover:cw-text-positive-muted",
  variable: "cw-rounded-md cw-px-1 cw-py-1.5 cw-text-subtle cw-bg-neutral cw-font-normal",
  normalWeight: "cw-font-normal",
};

export const LIST_OF_CHAT_RECOMMENDATIONS = [
  {
    icon: (
      <div className={classNames(recStyles.iconContainer, "cw-bg-positive")}>
        <CalendarToday className={classNames(recStyles.icon, "cw-text-positive")} />
      </div>
    ),
    iconDisabled: (
      <div className={classNames(recStyles.iconContainer, "cw-bg-default")}>
        <CalendarToday
          fill={disabled_fg_ALLROLES}
          className={classNames(recStyles.icon, "cw-text-positive-disabled")}
        />
      </div>
    ),
    text: "Find time with @",
    html: (
      <span className={recStyles.container}>
        <Tooltip
          title={
            <div className={recStyles.tipContainer}>
              <strong className={recStyles.tipTitle}>Avoid calendar chaos</strong>
              <div>Schedule meetings seamlessly with 1 or multiple people </div>
            </div>
          }
          placement="top"
        >
          <span className={recStyles.action}>Find time</span>
        </Tooltip>{" "}
        <span className={recStyles.normalWeight}>with</span> <span className={recStyles.at}>@</span>{" "}
        <span className={recStyles.variable}>your teammate</span>
      </span>
    ),
  },
  {
    icon: (
      <div className={classNames(recStyles.iconContainer, "cw-bg-info")}>
        <Update className={classNames(recStyles.icon, "cw-text-info")} />
      </div>
    ),
    iconDisabled: (
      <div className={classNames(recStyles.iconContainer, "cw-bg-default")}>
        <Update
          fill={disabled_fg_ALLROLES}
          className={classNames(recStyles.icon, "cw-text-info-disabled")}
        />
      </div>
    ),
    text: "Reschedule @",
    html: (
      <span className={recStyles.container}>
        <Tooltip
          title={
            <div className={recStyles.tipContainer}>
              <strong className={recStyles.tipTitle}>Find new times. Always</strong>
              <div>Reschedule any meeting on your calendar</div>
            </div>
          }
          placement="top"
        >
          <span className={recStyles.action}>Reschedule</span>
        </Tooltip>{" "}
        <span className={recStyles.at}>@</span>{" "}
        <span className={recStyles.variable}>your meeting</span>
      </span>
    ),
  },
  {
    icon: (
      <div className={classNames(recStyles.iconContainer, "cw-bg-busy")}>
        <AddLink className={classNames(recStyles.icon, "cw-text-busy")} />
      </div>
    ),
    iconDisabled: (
      <div className={classNames(recStyles.iconContainer, "cw-bg-default")}>
        <AddLink className={classNames(recStyles.icon, "cw-text-busy-disabled")} />
      </div>
    ),
    text: "Create a 30-minute scheduling link",
    html: (
      <span className={recStyles.container}>
        <Tooltip
          title={
            <div className={recStyles.tipContainer}>
              <strong className={recStyles.tipTitle}>Clockwise Scheduling Links</strong>
              <div>
                Create a link and customize the meeting length, date range, and additional
                attendees.
              </div>
            </div>
          }
          placement="top"
        >
          <span className={recStyles.action}>Create a</span>
        </Tooltip>{" "}
        <span className={recStyles.variable}>30m</span>{" "}
        <span className={recStyles.normalWeight}>scheduling link</span>
      </span>
    ),
  },
];

export const FIX_MY_CONFLICTS_RECOMMENDATION = {
  icon: <FixMyConflicts className={classNames(recStyles.icon, "cw-text-warning cw-w-6 cw-h-6")} />,
  iconDisabled: (
    <FixMyConflicts
      fillBG="white"
      fill={disabled_fg_ALLROLES}
      className={classNames(recStyles.icon, "cw-text-warning-disabled cw-w-6 cw-h-6")}
    />
  ),
  text: "Fix my conflicts this week",
  html: (
    <span className={recStyles.container}>
      <Tooltip
        title={
          <div className={recStyles.tipContainer}>
            <strong className={recStyles.tipTitle}>Fix your conflicts</strong>
            <div>Receive smart suggestions for how to resolve your meeting conflicts </div>
          </div>
        }
        placement="top"
      >
        <span className={recStyles.action}>Fix my conflicts</span>
      </Tooltip>{" "}
      <span className={recStyles.normalWeight}>this week</span>
    </span>
  ),
};

export const SendChatRecommendationButton = ({
  text,
  html,
  icon,
  disabled,
}: {
  text: string;
  html?: React.ReactNode;
  icon: React.ReactNode;
  disabled?: boolean;
}) => {
  const updatePromptText = useUpdateSuggestedPrompt();
  const track = useTracking();

  const submitMessage = async () => {
    if (disabled) {
      return;
    }
    updatePromptText(text);
    track(TrackingEvents.CHAT.NUX.CHAT_RECOMMENDATION_CLICKED, { text });
  };

  return (
    <Tooltip title={disabled ? AI_EXCLUSION_TOOLTIP : ""} placement="top">
      <div
        onClick={submitMessage}
        className={classNames(
          "cw-w-full cw-flex cw-items-center cw-my-2",
          "cw-bg-default cw-rounded-xl cw-px-5 cw-py-2 ",
          "cw-border cw-border-[transparent] cw-border-solid",
          "cw-transition cw-ease-out cw-duration-200",
          "cw-body-sm",
          {
            "cw-group hover:cw-border-positive-interactable-pressed cw-cursor-pointer": !disabled,
            "cw-cursor-not-allowed": disabled,
          },
        )}
        style={{
          boxShadow:
            "0px 1px 1px -1px rgba(0, 0, 0, 0.08), 0px 2px 4px -1px rgba(0, 0, 0, 0.12), 0px 0px 0px 1px rgba(0, 0, 0, 0.06)",
        }}
      >
        <div className="cw-flex cw-mr-4">{icon}</div>
        {disabled ? <div className="cw-text-positive-disabled">{text}</div> : html || text}
        <SendRounded className="cw-ml-auto cw-text-positive-muted cw-invisible group-hover:cw-visible" />
      </div>
    </Tooltip>
  );
};
